import React, { useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';
import { TrendingUp } from 'lucide-react';
import {
  ModalShell, Body, FieldRow, Field, Label, Input, Footer, Button, ErrorBanner
} from './reModalShared';

import { DateField } from '../shared/DateField';

interface PositionLite {
  id: number;
  propertyName: string;
  propertyType: string;
  location: string;
  currentValuation: number;
  capRate: number;     // decimal
  currentNoi: number;
}

interface Props {
  position: PositionLite;
  onClose: () => void;
  onSuccess: () => void;
}

const RealEstateAppraisalModal: React.FC<Props> = ({ position, onClose, onSuccess }) => {
  // Initialize from current silo values so the user can incrementally adjust.
  // capRate is stored as decimal in silo (0.055); display as % (5.50).
  const [appraisalDate, setAppraisalDate] = useState<string>(getTenantToday());
  const [appraisedValue, setAppraisedValue] = useState<string>(String(position.currentValuation || ''));
  const [capRatePct, setCapRatePct] = useState<string>(position.capRate ? (position.capRate * 100).toFixed(2) : '');
  const [noi, setNoi] = useState<string>(String(position.currentNoi || ''));
  const [appraisalFirm, setAppraisalFirm] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!appraisedValue || Number(appraisedValue) <= 0) {
      setError('Appraised value must be greater than 0');
      return;
    }
    setSubmitting(true);
    try {
      // Backend stores capRate as decimal — convert from %.
      const capRateDecimal = capRatePct ? Number(capRatePct) / 100 : null;
      await axios.put(`/api/credit/re-equity/${position.id}/appraisal`, {
        appraisalDate,
        appraisedValue: Number(appraisedValue),
        capRate: capRateDecimal,
        noi: noi ? Number(noi) : null,
        appraisalFirm: appraisalFirm || null
      });
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to record appraisal';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Record Appraisal"
      subtitle={`${position.propertyName} • ${position.propertyType} • ${position.location}`}
      icon={<TrendingUp size={18} className="text-violet-400" />}
      onClose={onClose}
    >
      <Body>
        <FieldRow>
          <Field>
            <Label>Appraisal Date</Label>
            <DateField value={appraisalDate ?? ''} onChange={iso => setAppraisalDate(iso)} />
          </Field>
          <Field>
            <Label>Appraisal Firm</Label>
            <Input
              type="text"
              placeholder="CBRE, JLL, Cushman & Wakefield…"
              value={appraisalFirm}
              onChange={e => setAppraisalFirm(e.target.value)}
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Appraised Value (USD)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="e.g. 130000000"
              value={appraisedValue}
              onChange={e => setAppraisedValue(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Cap Rate (%)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="e.g. 5.50"
              value={capRatePct}
              onChange={e => setCapRatePct(e.target.value)}
            />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Net Operating Income (USD)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="e.g. 7150000"
              value={noi}
              onChange={e => setNoi(e.target.value)}
            />
          </Field>
          <Field>
            <Label>Method</Label>
            <Input type="text" value="APPRAISAL" disabled />
          </Field>
        </FieldRow>

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>

      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $primary onClick={submit} disabled={submitting}>
          {submitting ? 'Posting MTM…' : 'Record Appraisal'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

export default RealEstateAppraisalModal;
